<template>
  <div v-bind:class="[large ? 'vue-wrapper' : 'skeleton-container', dashboarditem.loading || dashboarditem.updating ? 'active' : '']">
    <TheLoader v-if="large" :small="false" :active="dashboarditem.loading || dashboarditem.updating" :update="dashboarditem.updating"/>

    <table v-else class="clickable oddeven sortable skeleton-content">
      <thead>
        <tr>
          <th v-for="(thClass, thIndex) in skeletonThLines" v-bind:key="'sk-' + thIndex" v-bind:class="[thClass]"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(trLine, trIndex) in skeletonTdLines" v-bind:key="'sk-tr-' + trIndex">
          <td v-for="(tdClass, tdIndex) in trLine" v-bind:key="'sk-td-' + trIndex + '-' + tdIndex " v-bind:class="[tdClass]"></td>
        </tr>
      </tbody>
    </table>

    <section class="table-card-container">
      <table class="clickable visible oddeven sortable" :class="{'fixed': large}">
        <thead class="border">
          <tr>
            <th v-for="(heading, index) in dashboarditem.headings" v-bind:key="index" v-bind:class="[heading.class, heading.sortKey === dashboarditem.sort ? sortClassUnsigned : '']" @click="changeSorting(heading.sortKey)">
              {{heading.name}}
              <span id="sorttable_sortrevind" v-if="heading.sortKey === dashboarditem.sort && dashboarditem.direction === 'desc'">&nbsp;</span>
              <span id="sorttable_sortfwdind" v-if="heading.sortKey === dashboarditem.sort && dashboarditem.direction === 'asc'">&nbsp;</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in dashboarditem.items" v-bind:key="item.Objektid" @click="showObjektItem(item)">
            <td>
              <div class="flex-container">
                <span class="thumbnail-container">
                  <span class="thumbnail small placeholder animation">
                    <template v-if="objektThumb(item) && objektLarge(item)">
                      <img class="hi-trigger" :src="objektThumb(item)" :data-hi-src-full="objektLarge(item)" data-hi-size="large" />
                    </template>
                    <template v-else-if="objektThumb(item)">
                      <img :src="objektThumb(item)" />
                    </template>
                    <template v-else>
                      <span class="placeholder"></span>
                    </template>
                  </span>
                  <a href="javascript:;" class="primary-title">{{item.Objektnr}}</a>
                </span>
              </div>
            </td>
            <td class="nowrap" v-if="item.subtitle" v-tippy :content="`${item.title}<br>${item.subtitle}`">
              <span class="tippy-right">{{item.title}}</span>
              <span class="small opacity-60">{{item.subtitle}}</span>
            </td>
            <td class="nowrap" v-else-if="item.title" v-tippy :content="`${item.title}`">
              <span class="tippy-right">{{item.title}}</span>
            </td>
            <td class="nowrap" v-else>
              <span class="tippy-right"></span>
            </td>
            <td class="align-right">{{item.Auksjon}}</td>
            <td class="align-right statusicon">
              <span v-if="item.Tekst_ok === 'T'" class="statusicon verified"><svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-success"></use></svg></span>
              <span v-else class="statusicon unverified"><svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-error"></use></svg></span>
            </td>
            <td class="align-right statusicon">
              <span v-if="item.Fototatt === 'T'" class="statusicon verified"><svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-success"></use></svg></span>
              <span v-else class="statusicon unverified"><svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-error"></use></svg></span>
            </td>
            <td class="align-right statusicon">
              <span v-if="item.kontrakt.Registrertsignert" class="statusicon verified"><svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-success"></use></svg></span>
              <span v-else class="statusicon unverified"><svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-error"></use></svg></span>
            </td>
            <td class="align-right">
              <span class="opacity-60">{{item.Sist_endret_tid | avantidatetime}}</span>
            </td>
            <td class="align-right">
              <span v-if="item.kontraktsansvarlig" class="avatar tiny name left" :class="strToAvatarClass(item.kontraktsansvarlig.initials)">{{item.kontraktsansvarlig.initials}} <span class="name">{{item.kontraktsansvarlig.full_name || item.kontraktsansvarlig.initials}}</span></span>
              <span v-else class="avatar tiny name left" :class="strToAvatarClass('-')">- <span class="name">Ingen ansvarlig</span></span>
            </td>
          </tr>
        </tbody>

        <template v-if="large">
          <tfoot v-if="dashboarditem.pages > 1">
            <tr>
              <td :colspan="dashboarditem.headings.length">
                <div class="pagination-container" style="margin-top: 0; padding: 1rem 0;">
                  <paginate v-model="dashboarditem.page"
                    :page-count="dashboarditem.pages"
                    :page-range="3"
                    :click-handler="setPage"
                    :container-class="'pagination'"
                    :prev-text="paginationArrow"
                    :next-text="paginationArrow"
                    :prev-class="'prev'"
                    :next-class="'next'"
                    >
                  </paginate>
                </div>
              </td>
            </tr>
          </tfoot>
        </template>
      </table>

      <div v-if="dashboarditem.items.length < 1" class="placeholder-container center-container active">
        <div class="center-content">
          <div class="placeholder-icon tiny"><svg width="24px" height="24px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-search"></use></svg></div>
          <h4 class="bold">Fant ingen treff</h4>
          <p class="no-margin">Valgt filter returnerte ingen treff.</p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import ObjektService from '@/services/ObjektService'
import Vue from 'vue'
import VueTippy from 'vue-tippy'
import bus from '@/plugins/event-bus'

Vue.use(VueTippy, {
  directive: 'tippy',
  duration: 0,
  delay: 0,
  followCursor: false,
  arrow: true,
  animateFill: false,
  interactive: false,
  allowHTML: true,
  animation: 'fade',
  placement: 'right',
  popperOptions: {
    modifiers: {
      preventOverflow: {
        enabled: true
      }
    }
  }
})

export default {
  name: 'TableUferdigeObjekt',
  props: {
    large: {
      type: Boolean,
      required: false,
      default () {
        return true
      }
    },
    limit: {
      type: Number,
      required: true
    },
    brukerId: {
      required: true
    }
  },
  data () {
    return {
      // hasHover: false,
      dashboarditem: {
        page: 1,
        pages: 1,
        total: 0,
        items: [],
        loading: true,
        updating: false,
        sort: 'Objektid',
        direction: 'desc',
        headings: [
          { name: 'Objekt', sortKey: 'Objektnr', class: '', skelTh: 'sk-text sk-text-narrow', skelTd: 'sk-thumb sk-thumb-small' },
          { name: 'Tittel', sortKey: 'Tittel', class: 'nowrap', skelTh: 'nowrap sk-text sk-text-narrow', skelTd: 'no-wrap sk-text sk-text-wide' },
          { name: 'Auksjon', sortKey: 'Auksjon', class: 'align-right', skelTh: 'align-right sk-text sk-text-narrow', skelTd: 'align-right sk-text sk-text-narrow' },
          { name: 'Obj. ', sortKey: 'Tekst_ok', class: 'align-right statusicon', skelTh: 'align-right statusicon', skelTd: 'align-right statusicon' },
          { name: 'Foto', sortKey: 'Fototatt', class: 'align-right statusicon', skelTh: 'align-right statusicon', skelTd: 'align-right statusicon' },
          { name: 'Sign.', sortKey: 'kontrakt.Registrertsignert', class: 'align-right statusicon', skelTh: 'align-right statusicon', skelTd: 'align-right statusicon' },
          { name: 'Endret', sortKey: 'Sist_endret_tid', class: 'align-right', skelTh: 'align-right sk-text sk-text-narrow', skelTd: 'align-right sk-text sk-text-narrow' },
          { name: 'Ansv.', sortKey: 'kontrakt.mottaker.Fornavn', class: 'align-right', skelTh: 'align-right sk-text sk-text-narrow', skelTd: 'align-right sk-circle sk-circle-tiny' }
        ]
      }
    }
  },
  filters: {
    toInteger (value) {
      return !value ? 0 : parseInt(value)
    }
  },
  created () {
    bus.$off('kunde-kontrakt-objekt-updated')

    this.dashboarditem.loading = true

    this.fetch()
  },
  mounted () {
    bus.$on('kunde-kontrakt-objekt-updated', (item) => this.onObjektUpdated(item))
  },
  computed: {
    sortClassUnsigned: function () {
      return 'sorttable_sorted' + (this.dashboarditem.direction !== 'asc' ? '_reverse' : '')
    },
    skeletonThLines: function () {
      return this.dashboarditem.headings.map(o => o.skelTh)
    },
    skeletonTdLines: function () {
      const retval = []

      const tdvals = this.dashboarditem.headings.map(o => o.skelTd)

      for (let i = 0; i < this.limit; ++i) {
        retval.push(tdvals)
      }

      return retval
    }
  },
  watch: {
    brukerId: function (newVal) {
      if (newVal !== undefined) {
        this.dashboarditem.page = 1
        this.dashboarditem.updating = true
        this.fetch()
      }
    }
  },
  methods: {
    onObjektUpdated: function (item) {
      const found = this.dashboarditem.items.findIndex(o => o.Objektid === item.Objektid)

      if (found !== -1) {
        // Update item
        this.$set(this.dashboarditem.items, found, item)
      }
    },
    setPage: function (page) {
      const vm = this

      window.jQuery('html').animate({
        scrollTop: 0
      }, 200, function () {
        vm.dashboarditem.page = page
        vm.dashboarditem.updating = true
        vm.fetch()
      })
    },
    changeSorting: function (sortKey) {
      if (!sortKey) {
        return
      }

      if (this.dashboarditem.sort === sortKey) {
        this.dashboarditem.direction = this.dashboarditem.direction === 'asc' ? 'desc' : 'asc'
      } else {
        this.dashboarditem.direction = 'asc'
      }

      this.dashboarditem.sort = sortKey
      this.dashboarditem.page = 1
      this.dashboarditem.updating = true
      this.fetch()
    },
    fetch: function () {
      let queryParams = {
        page: this.dashboarditem.page,
        sort: this.dashboarditem.sort,
        direction: this.dashboarditem.direction,
        limit: this.limit,
        ansvarlig: this.brukerId
      }

      ObjektService.getNotReady(queryParams)
        .then((response) => {
          this.dashboarditem.total = response.headers['x-paging-count'] ? parseInt(response.headers['x-paging-count']) : 0
          this.dashboarditem.pages = response.headers['x-paging-pagecount'] ? parseInt(response.headers['x-paging-pagecount']) : 1
          this.dashboarditem.items = response.data
        })
        .catch((error) => {
          this.$helpers.showErrorAlert('Feil', 'Kunne ikke laste inn uferdige objekter')
          console.log(error)
        })
        .then(() => {
          this.dashboarditem.loading = false
          this.dashboarditem.updating = false

          // Maybe trigger a load of all images that isn't loaded
          // Best way would be to listen to a websocket that loads photos

          this.$emit('tableLoaded', this.dashboarditem)
        })
    },
    objektThumb: function (item) {
      if (item.photos && item.photos.thumb && item.photos.thumb.length > 0) {
        return item.photos.thumb[0]
      }

      return null
    },
    objektLarge: function (item) {
      if (item.photos && item.photos.large && item.photos.large.length > 0) {
        return item.photos.large[0]
      }

      return null
    },
    showObjektItem: function (item) {
      window.jQuery('#hi-container').removeClass('active')
      window.jQuery(document).unbind('mousemove')

      this.loadObjektModal(item.Objektid)

      // this.$router.push({
      //   name: 'KundeCard',
      //   params: { kunde_id: item.kontrakt.Kundenr },
      //   query: { kontrakt: item.Kontraktnr, objekt: item.Objektid },
      //   hash: ''
      // })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Admin/Dashboard/TableUferdigeObjekt.vue